import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShoppingBag, Check, Mail, Activity, Wand2, Loader2 } from "lucide-react";

export default function Integrations() {
  const { user, checkAuth } = useAuth();
  const [store, setStore] = useState(user?.shopify_store || "");
  const [shopifyCfg, setShopifyCfg] = useState({ configured: false, app_url_set: false });
  const [klaviyoKey, setKlaviyoKey] = useState("");
  const [demoActive, setDemoActive] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);

  useEffect(() => {
    api.get("/shopify/config-status").then(({ data }) => setShopifyCfg(data)).catch(() => {});
    api.get("/demo/status").then(({ data }) => setDemoActive(data.active)).catch(() => {});
    // If we just returned from Shopify OAuth, refresh user state
    if (new URLSearchParams(window.location.search).get("shopify") === "connected") {
      checkAuth();
    }
  }, []);

  const toggleDemo = async () => {
    setDemoLoading(true);
    try {
      if (demoActive) {
        await api.post("/giveaways/clear-demo");
        setDemoActive(false);
        toast.success("Demo data cleared");
      } else {
        await api.post("/giveaways/seed-demo");
        setDemoActive(true);
        toast.success("Demo data loaded — check your dashboard");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setDemoLoading(false);
    }
  };

  const connectShopify = async (e) => {
    e.preventDefault();
    if (!store) { toast.error("Store URL required"); return; }
    if (shopifyCfg.configured && shopifyCfg.app_url_set) {
      // Real OAuth flow
      try {
        const { data } = await api.get(`/shopify/install?shop=${encodeURIComponent(store)}`);
        window.location.href = data.install_url;
      } catch (err) { toast.error(err.response?.data?.detail || "Install failed"); }
    } else {
      // Manual fallback
      try {
        await api.post("/shopify/connect", { store_url: store });
        toast.success("Shopify store connected (manual mode)");
        await checkAuth();
      } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    }
  };

  const reconnectShopify = async () => {
    const s = user?.shopify_store;
    if (!s) return;
    if (shopifyCfg.configured && shopifyCfg.app_url_set) {
      try {
        const { data } = await api.get(`/shopify/install?shop=${encodeURIComponent(s)}`);
        window.location.href = data.install_url;
      } catch (err) { toast.error(err.response?.data?.detail || "Reconnect failed"); }
    } else {
      toast.error("OAuth not configured");
    }
  };

  const disconnectShopify = async () => {
    await api.post("/shopify/disconnect");
    toast.success("Disconnected");
    setStore("");
    await checkAuth();
  };

  const connectKlaviyo = async (e) => {
    e.preventDefault();
    if (!klaviyoKey) { toast.error("Klaviyo API key required"); return; }
    try {
      const { data } = await api.post("/klaviyo/connect", { api_key: klaviyoKey });
      toast.success(`Klaviyo connected (mock) — will fire "${data.event_name}"`);
      setKlaviyoKey("");
      await checkAuth();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const disconnectKlaviyo = async () => {
    await api.post("/klaviyo/disconnect");
    toast.success("Klaviyo disconnected");
    await checkAuth();
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-8 py-10">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Connect your stack</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter mt-2 mb-8">Integrations</h1>

        {/* Shopify */}
        <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="shopify-integration-card">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="size-12 rounded-md bg-emerald-50 flex items-center justify-center">
                <ShoppingBag className="size-5 text-emerald-600" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg">Shopify</h3>
                <p className="text-sm text-zinc-600 mt-1">Auto-track orders as giveaway entries based on your ratio.</p>
                <div className="text-[10px] uppercase tracking-[0.15em] font-semibold mt-2">
                  {shopifyCfg.configured && shopifyCfg.app_url_set
                    ? <span className="text-emerald-600">OAuth ready</span>
                    : <span className="text-amber-600">OAuth keys not set — using manual mode</span>}
                </div>
              </div>
            </div>
            {user?.shopify_connected && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                <Check className="size-3" strokeWidth={2} /> Connected
              </span>
            )}
          </div>

          {user?.shopify_connected ? (
            <div className="mt-5 pt-5 border-t border-zinc-100 flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Store</div>
                <div className="font-mono text-sm mt-1">{user.shopify_store}</div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={reconnectShopify} data-testid="shopify-reconnect-button" className="border border-emerald-200 text-emerald-700 px-3 py-2 rounded-md text-xs font-medium hover:bg-emerald-50">
                  Reconnect / refresh
                </button>
                <button onClick={disconnectShopify} data-testid="shopify-disconnect-button" className="border border-zinc-200 px-3 py-2 rounded-md text-xs font-medium hover:bg-zinc-50">
                  Disconnect
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={connectShopify} className="mt-5 pt-5 border-t border-zinc-100 flex items-center gap-3">
              <input
                value={store}
                onChange={(e) => setStore(e.target.value)}
                placeholder="your-store.myshopify.com"
                data-testid="shopify-store-input"
                className="flex-1 border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none"
              />
              <button type="submit" data-testid="shopify-connect-button" className="bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800">
                {shopifyCfg.configured && shopifyCfg.app_url_set ? "Install via Shopify" : "Connect"}
              </button>
            </form>
          )}
        </div>

        {/* Klaviyo */}
        <div className="bg-white border border-zinc-200 rounded-md p-6 mt-4" data-testid="klaviyo-integration-card">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="size-12 rounded-md bg-zinc-100 flex items-center justify-center">
                <Mail className="size-5 text-zinc-700" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg">Klaviyo</h3>
                <p className="text-sm text-zinc-600 mt-1">Fires "SweepLaunch New Entry" event with entrant email, giveaway, count, referral source.</p>
                <div className="text-[10px] uppercase tracking-[0.15em] text-amber-600 font-semibold mt-2">Phase 2 — UI ready, event firing mocked</div>
              </div>
            </div>
            {user?.klaviyo_connected && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded">
                <Check className="size-3" strokeWidth={2} /> Connected
              </span>
            )}
          </div>
          {user?.klaviyo_connected ? (
            <div className="mt-5 pt-5 border-t border-zinc-100 flex items-center justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">API Key</div>
                <div className="font-mono text-sm mt-1">pk_••••••••{user.klaviyo_key_last4}</div>
              </div>
              <button onClick={disconnectKlaviyo} data-testid="klaviyo-disconnect-button" className="border border-zinc-200 px-3 py-2 rounded-md text-xs font-medium hover:bg-zinc-50">
                Disconnect
              </button>
            </div>
          ) : (
            <form onSubmit={connectKlaviyo} className="mt-5 pt-5 border-t border-zinc-100 flex items-center gap-3">
              <input
                value={klaviyoKey}
                onChange={(e) => setKlaviyoKey(e.target.value)}
                placeholder="pk_live_..."
                data-testid="klaviyo-key-input"
                type="password"
                className="flex-1 border border-zinc-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 focus:outline-none"
              />
              <button type="submit" data-testid="klaviyo-connect-button" className="bg-zinc-950 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-zinc-800">
                Connect
              </button>
            </form>
          )}
        </div>

        {/* Demo Mode toggle */}
        <div className="bg-white border border-zinc-200 rounded-md p-6 mt-4" data-testid="demo-mode-card">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="size-12 rounded-md bg-amber-50 flex items-center justify-center">
                <Wand2 className="size-5 text-amber-600" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg">Demo Mode</h3>
                <p className="text-sm text-zinc-600 mt-1 max-w-md">Populate your dashboard with a sample "Yamaha YFZ450R Giveaway" — 612 entrants, $18,250 tracked revenue, fraud flags and leaderboard. Clearly labeled and removable in one click.</p>
                <div className="text-[10px] uppercase tracking-[0.15em] font-semibold mt-2">
                  {demoActive ? <span className="text-emerald-600">Demo data active</span> : <span className="text-zinc-500">Off</span>}
                </div>
              </div>
            </div>
            <button
              onClick={toggleDemo}
              disabled={demoLoading}
              role="switch"
              aria-checked={demoActive}
              data-testid="demo-mode-toggle"
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${demoActive ? "bg-emerald-500" : "bg-zinc-300"}`}
            >
              {demoLoading
                ? <Loader2 className="size-3.5 animate-spin text-white mx-auto" strokeWidth={2} />
                : <span className={`inline-block size-4 transform rounded-full bg-white transition-transform ${demoActive ? "translate-x-6" : "translate-x-1"}`} />}
            </button>
          </div>
        </div>

        {/* Meta Pixel — coming soon */}
        <div className="bg-white border border-zinc-200 rounded-md p-5 mt-4 opacity-70" data-testid="integration-meta-pixel">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-md bg-zinc-100 flex items-center justify-center">
              <Activity className="size-4 text-zinc-700" strokeWidth={1.5} />
            </div>
            <div>
              <div className="font-display font-bold">Meta Pixel</div>
              <div className="text-xs text-zinc-500 mt-0.5">Server-side conversion events for ad optimization.</div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold mt-3">Coming soon</div>
        </div>
      </div>
    </Layout>
  );
}
